import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from './shared/ss-select-tooltip.directive';
import { firstValueFrom } from 'rxjs';
import { StreamingTextComponent } from '../global-components/streaming-text/streaming-text.component';
import {
  SelfServiceChatAnalysisResult,
  SelfServiceChatMissingField,
  SelfServiceChatSaveResult,
  SelfServiceChatService,
} from './shared/self-service-chat.service';
import { SelfServiceDatasetService } from './shared/self-service-dataset.service';

interface SelfServiceChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  createdAt: string;
}

interface SelfServiceChatConversation {
  id: string;
  title: string;
  subtitle: string;
  updatedAt: string;
  active: boolean;
}

interface TemplateKeyMapping {
  originalKey: string;
  nextKey: string;
  originalDisplayLabel: string;
  displayLabel: string;
  usagePaths: string[];
}

@Component({
  selector: 'app-self-service-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    SsSelectTooltipDirective,
    StreamingTextComponent,
  ],
  templateUrl: './self-service-chat.component.html',
  styleUrl: './self-service-chat.component.scss',
})
export class SelfServiceChatComponent implements OnChanges, OnDestroy {
  private static readonly HIDDEN_ENTITY_COLUMNS = new Set([
    'uuid',
    'created_at',
    'updated_date',
    'created_date',
    'updated_by',
    'created_by',
  ]);
  private static readonly TEMPLATE_MAPPING_VALUE_KEYS = new Set([
    'key',
    'options',
    'dataKey',
    'categoryKey',
    'valueKey',
    'xAxis',
    'xAxis2',
    'yAxis',
    'yAxis2',
    'legendKey',
    'filterKey',
    'seriesKey',
    'labelKey',
    'nameKey',
    'sizeKey',
    'colorKey',
  ]);

  @Input() selectedEntity: string | null = null;
  @Input() availableColumns: string[] = [];
  @Input() dashboardName = '';
  @Output() closed = new EventEmitter<void>();
  @Output() missingFieldSelectionsChange = new EventEmitter<Record<string, string>>();

  readonly conversations: SelfServiceChatConversation[] = [
    {
      id: 'self-service-support',
      title: 'Support Request',
      subtitle: 'Waiting for file',
      updatedAt: 'Now',
      active: true,
    },
  ];

  messages: SelfServiceChatMessage[] = [];
  composerText = '';
  selectedFiles: File[] = [];
  isLoading = false;
  uploadError = '';
  analysis?: SelfServiceChatAnalysisResult;
  fieldSelections: Record<string, string> = {};
  activeConversationId = this.conversations[0].id;
  templateOriginal: Record<string, unknown> | null = null;
  templatePreview: Record<string, unknown> | null = null;
  templatePreviewDraft = '';
  templatePreviewDraftError = '';
  templateEntityNameOriginal = '<entity_name>';
  templateEntityName = '<entity_name>';
  templateKeyMappings: TemplateKeyMapping[] = [];
  templateSearch = '';
  templateValidationErrors: string[] = [];
  templateValidationMessage = '';
  templateSaveMessage = '';
  entityOptions: string[] = [];
  selectedTemplateEntity = '';
  entityColumns: string[] = [];
  entityRowsPreview: Array<Record<string, unknown>> = [];
  entityLoadError = '';
  isEntityOptionsLoading = false;
  isEntityColumnsLoading = false;
  isTemplateSaving = false;
  generatedDashboardName = '';
  generatedDashboardSlug = '';
  private savedTemplateSnapshot = '';

  private readonly objectUrlMap = new Map<File, string>();

  constructor(
    private readonly selfServiceChatService: SelfServiceChatService,
    private readonly selfServiceDatasetService: SelfServiceDatasetService,
  ) {
    this.resetConversation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableColumns'] && this.analysis?.missingFields?.length) {
      this.refreshMissingFieldOptions();
    }
  }

  ngOnDestroy(): void {
    this.revokeAllPreviewUrls();
  }

  get activeConversation(): SelfServiceChatConversation {
    return this.conversations.find((conversation) => conversation.id === this.activeConversationId) || this.conversations[0];
  }

  get missingFields(): SelfServiceChatMissingField[] {
    return this.analysis?.missingFields || [];
  }

  get canSubmit(): boolean {
    return !this.isLoading && this.selectedFiles.length > 0;
  }

  get hasTemplateEditor(): boolean {
    return !!this.templateOriginal && !!this.templatePreview;
  }

  get filteredTemplateKeyMappings(): TemplateKeyMapping[] {
    const search = this.templateSearch.trim().toLowerCase();
    if (!search) {
      return this.templateKeyMappings;
    }

    return this.templateKeyMappings.filter((mapping) => (
      mapping.originalKey.toLowerCase().includes(search)
      || mapping.nextKey.toLowerCase().includes(search)
      || mapping.displayLabel.toLowerCase().includes(search)
      || mapping.usagePaths.some((path) => path.toLowerCase().includes(search))
    ));
  }

  get templatePreviewJson(): string {
    return this.templatePreviewDraft || (this.templatePreview ? JSON.stringify(this.templatePreview, null, 2) : '');
  }

  get templateChangedCount(): number {
    return this.templateKeyMappings.filter((mapping) => (
      mapping.nextKey.trim() !== mapping.originalKey || mapping.displayLabel !== mapping.originalDisplayLabel
    )).length;
  }

  get templateUsageCount(): number {
    return this.templateKeyMappings.reduce((count, mapping) => count + mapping.usagePaths.length, 0);
  }

  get mappedTemplateKeyCount(): number {
    return this.templateKeyMappings.filter((mapping) => this.isTemplateKeyMappedToEntityColumn(mapping)).length;
  }

  get unresolvedTemplateKeyCount(): number {
    return this.templateKeyMappings.filter((mapping) => this.templateKeyNeedsAttention(mapping)).length;
  }

  get hasEntityColumns(): boolean {
    return this.entityColumns.length > 0;
  }

  startNewChat(): void {
    this.revokeAllPreviewUrls();
    this.resetConversation();
  }

  closeChat(): void {
    this.closed.emit();
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) {
      return;
    }

    const outgoingFiles = [...this.selectedFiles];
    const outgoingMessage = this.composerText.trim();
    this.pushMessage({
      text: outgoingMessage || 'Attached file for analysis.',
      sender: 'user',
      files: outgoingFiles,
    });

    this.uploadError = '';
    this.composerText = '';
    this.selectedFiles = [];
    this.isLoading = true;

    try {
      const result = await firstValueFrom(this.selfServiceChatService.analyzeFiles(outgoingFiles, {
        dashboardName: this.dashboardName,
        availableColumns: this.availableColumns,
      }));

      if (!result) {
        throw new Error('No response received from the file analysis service.');
      }

      this.analysis = result;
      this.generatedDashboardName = result.dashboardName;
      this.generatedDashboardSlug = result.dashboardSlug;
      this.initializeFieldSelections(result.missingFields);
      this.initializeTemplateEditor(result.submitPayloadTemplate);
      this.pushMessage({
        text: result.message,
        sender: 'agent',
      });
      this.updateConversationSubtitle(
        result.submitPayloadTemplate
          ? 'Template ready for review'
          : result.missingFields.length
          ? `${result.missingFields.length} missing field${result.missingFields.length === 1 ? '' : 's'} found`
          : 'Analysis complete',
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to analyze the uploaded file.';
      this.uploadError = message;
      this.pushMessage({
        text: message,
        sender: 'agent',
      });
      this.updateConversationSubtitle('Request failed');
    } finally {
      this.isLoading = false;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) {
      this.selectedFiles = [...this.selectedFiles, ...files];
    }
    input.value = '';
  }

  removeFile(fileToRemove: File): void {
    const previewUrl = this.objectUrlMap.get(fileToRemove);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      this.objectUrlMap.delete(fileToRemove);
    }
    this.selectedFiles = this.selectedFiles.filter((file) => file !== fileToRemove);
  }

  onMissingFieldSelectionChange(fieldKey: string, value: string | null): void {
    this.fieldSelections = {
      ...this.fieldSelections,
      [fieldKey]: value || '',
    };
    this.missingFieldSelectionsChange.emit({ ...this.fieldSelections });
  }

  isImageFile(file: File): boolean {
    return typeof file?.type === 'string' && file.type.startsWith('image/');
  }

  getFilePreviewUrl(file: File): string | null {
    if (!this.isImageFile(file)) {
      return null;
    }

    const cached = this.objectUrlMap.get(file);
    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    this.objectUrlMap.set(file, url);
    return url;
  }

  trackTemplateKeyMapping(_index: number, mapping: TemplateKeyMapping): string {
    return mapping.originalKey;
  }

  onTemplateEntityNameInput(value: string): void {
    this.templateEntityName = value;
    if (this.selectedTemplateEntity && this.selectedTemplateEntity !== value.trim()) {
      this.selectedTemplateEntity = '';
    }
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.rebuildTemplatePreview();
  }

  async onTemplateEntitySelectionChange(entityName: string | null): Promise<void> {
    const nextEntity = String(entityName || '').trim();
    this.selectedTemplateEntity = nextEntity;
    this.entityLoadError = '';
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';

    if (!nextEntity) {
      this.entityColumns = [];
      this.entityRowsPreview = [];
      return;
    }

    this.templateEntityName = nextEntity;
    this.rebuildTemplatePreview();
    await this.loadEntityColumns(nextEntity);
  }

  onTemplateKeyInput(mapping: TemplateKeyMapping, value: string): void {
    mapping.nextKey = value;
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.rebuildTemplatePreview();
  }

  onTemplateEntityColumnSelect(mapping: TemplateKeyMapping, value: string | null): void {
    if (!value) {
      return;
    }

    mapping.nextKey = value;
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.rebuildTemplatePreview();
  }

  onTemplateLabelInput(mapping: TemplateKeyMapping, value: string): void {
    mapping.displayLabel = value;
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.rebuildTemplatePreview();
  }

  onTemplatePreviewJsonInput(value: string): void {
    this.templatePreviewDraft = value;
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';

    const parsed = this.parseTemplateDraft(value);
    if (!parsed) {
      return;
    }

    this.templatePreview = parsed;
    this.templatePreviewDraftError = '';
  }

  formatTemplatePreviewJson(): void {
    const parsed = this.parseTemplateDraft(this.templatePreviewDraft);
    if (!parsed) {
      return;
    }

    this.templatePreview = parsed;
    this.templatePreviewDraft = JSON.stringify(parsed, null, 2);
    this.templatePreviewDraftError = '';
  }

  autoMatchTemplateKeys(showFeedback = true): void {
    let changed = false;
    this.templateKeyMappings.forEach((mapping) => {
      if (!this.shouldAutoMatchTemplateKey(mapping)) {
        return;
      }

      const bestSuggestion = this.getRankedTemplateKeySuggestions(mapping)[0];
      if (bestSuggestion && bestSuggestion.score >= 85 && mapping.nextKey !== bestSuggestion.column) {
        mapping.nextKey = bestSuggestion.column;
        changed = true;
      }
    });

    if (!changed) {
      if (!showFeedback) {
        return;
      }
      this.templateValidationMessage = this.entityColumns.length
        ? 'No confident column matches were found automatically. Use search and the dropdowns to finish the mapping.'
        : 'Load an entity first to enable automatic column matching.';
      this.templateSaveMessage = '';
      return;
    }

    if (showFeedback) {
      this.templateValidationMessage = 'Mapped the strongest matches to entity columns. Review the highlighted rows before saving.';
      this.templateSaveMessage = '';
    }
    this.rebuildTemplatePreview();
  }

  isTemplateKeyMappedToEntityColumn(mapping: TemplateKeyMapping): boolean {
    const nextKey = mapping.nextKey.trim();
    return !!nextKey && this.entityColumns.includes(nextKey);
  }

  templateKeyNeedsAttention(mapping: TemplateKeyMapping): boolean {
    const nextKey = mapping.nextKey.trim();
    return !nextKey || this.isBackendMappingPlaceholder(nextKey) || !this.isValidTargetBusinessKey(nextKey);
  }

  getTemplateKeyStatusLabel(mapping: TemplateKeyMapping): string {
    if (this.isTemplateKeyMappedToEntityColumn(mapping)) {
      return 'Mapped';
    }
    if (!mapping.nextKey.trim()) {
      return 'Empty';
    }
    if (this.isBackendMappingPlaceholder(mapping.nextKey.trim())) {
      return 'Needs mapping';
    }
    if (!this.isValidTargetBusinessKey(mapping.nextKey.trim())) {
      return 'Invalid';
    }
    return mapping.nextKey.trim() === mapping.originalKey ? 'Original key' : 'Edited';
  }

  getTemplateKeySuggestions(mapping: TemplateKeyMapping): string[] {
    return this.getRankedTemplateKeySuggestions(mapping)
      .slice(0, 3)
      .map((suggestion) => suggestion.column);
  }

  resetTemplateEditor(): void {
    if (!this.templateOriginal) {
      return;
    }

    this.templateEntityName = this.templateEntityNameOriginal;
    this.templateKeyMappings = this.collectTemplateKeyMappings(this.templateOriginal);
    this.templateValidationErrors = [];
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.rebuildTemplatePreview();
  }

  validateTemplateEditor(showSuccess = true): boolean {
    const errors: string[] = [];
    const parsedPreview = this.parseTemplateDraft(this.templatePreviewDraft);
    if (!parsedPreview) {
      errors.push(this.templatePreviewDraftError || 'The submitPayloadTemplate JSON editor contains invalid JSON.');
    } else {
      this.templatePreview = parsedPreview;
    }

    const entityName = this.templateEntityName.trim();
    if (!entityName) {
      errors.push('Entity name is required.');
    }

    const seen = new Set<string>();
    this.templateKeyMappings.forEach((mapping) => {
      const nextKey = mapping.nextKey.trim();
      if (!nextKey) {
        errors.push(`Business key "${mapping.originalKey}" cannot be empty.`);
        return;
      }

      if (!this.isValidTargetBusinessKey(nextKey)) {
        errors.push(`Business key "${mapping.originalKey}" must start with a letter or underscore and contain only letters, digits, or underscores.`);
        return;
      }

      if (nextKey.toLowerCase() === 'uuid') {
        errors.push(`Business key "${mapping.originalKey}" cannot be renamed to "uuid".`);
        return;
      }

      if (seen.has(nextKey)) {
        errors.push(`Duplicate business key name "${nextKey}" is not allowed.`);
        return;
      }
      seen.add(nextKey);
    });

    this.templateValidationErrors = errors;
    this.templateValidationMessage = errors.length
      ? ''
      : showSuccess
        ? 'Template is valid and ready to save.'
        : '';
    if (errors.length) {
      this.templateSaveMessage = '';
    }
    return errors.length === 0;
  }

  async saveTemplateEditor(): Promise<void> {
    if (!this.validateTemplateEditor(true) || !this.templatePreview) {
      return;
    }

    const saveSlug = this.generatedDashboardSlug || this.resolveDashboardSlugFallback();
    if (!saveSlug) {
      this.templateValidationErrors = ['Dashboard slug is missing. Upload the PPT again before saving.'];
      this.templateValidationMessage = '';
      this.templateSaveMessage = '';
      return;
    }

    this.isTemplateSaving = true;
    try {
      const response = await firstValueFrom(
        this.selfServiceChatService.saveGeneratedDashboard(saveSlug, this.templatePreview),
      );
      this.savedTemplateSnapshot = JSON.stringify(this.templatePreview);
      this.handleTemplateSaveSuccess(response);
    } catch (error) {
      this.templateSaveMessage = '';
      this.templateValidationMessage = '';
      this.templateValidationErrors = [
        error instanceof Error ? error.message : 'Unable to save the edited dashboard JSON.',
      ];
    } finally {
      this.isTemplateSaving = false;
    }
  }

  private refreshMissingFieldOptions(): void {
    if (!this.analysis) {
      return;
    }

    this.analysis = {
      ...this.analysis,
      missingFields: this.analysis.missingFields.map((field) => ({
        ...field,
        options: field.options.length
          ? field.options
          : this.availableColumns.map((column) => ({ label: column, value: column })),
      })),
    };
  }

  private loadEntityOptions(): void {
    if (this.isEntityOptionsLoading || this.entityOptions.length) {
      return;
    }

    this.isEntityOptionsLoading = true;
    this.entityLoadError = '';
    this.selfServiceDatasetService.getEntities().subscribe({
      next: (entities) => {
        this.entityOptions = entities;
        this.isEntityOptionsLoading = false;
        if (!this.selectedTemplateEntity && this.selectedEntity && entities.includes(this.selectedEntity)) {
          this.selectedTemplateEntity = this.selectedEntity;
          this.templateEntityName = this.selectedEntity;
          this.rebuildTemplatePreview();
          void this.loadEntityColumns(this.selectedEntity);
        }
      },
      error: () => {
        this.entityOptions = [];
        this.isEntityOptionsLoading = false;
        this.entityLoadError = 'Unable to load entities.';
      },
    });
  }

  private async loadEntityColumns(entityName: string): Promise<void> {
    this.isEntityColumnsLoading = true;
    this.entityLoadError = '';

    try {
      const rows = await firstValueFrom(this.selfServiceDatasetService.getEntityRows(entityName));
      this.entityColumns = this.extractEntityColumns(rows);
      this.entityRowsPreview = rows.slice(0, 5);
      if (this.templateKeyMappings.length) {
        this.autoMatchTemplateKeys(false);
      }
    } catch (error) {
      this.entityColumns = [];
      this.entityRowsPreview = [];
      this.entityLoadError = error instanceof Error ? error.message : 'Unable to load entity columns.';
    } finally {
      this.isEntityColumnsLoading = false;
    }
  }

  private extractEntityColumns(rows: Array<Record<string, unknown>>): string[] {
    const columns: string[] = [];
    const seen = new Set<string>();

    rows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (SelfServiceChatComponent.HIDDEN_ENTITY_COLUMNS.has(key) || seen.has(key)) {
          return;
        }
        seen.add(key);
        columns.push(key);
      });
    });

    return columns;
  }

  private handleTemplateSaveSuccess(response: SelfServiceChatSaveResult): void {
    this.templateValidationErrors = [];
    this.templateValidationMessage = '';
    this.templateSaveMessage = response.message || 'Updated DVM JSON saved successfully.';
    this.pushMessage({
      sender: 'agent',
      text: this.templateSaveMessage,
    });
  }

  private resolveDashboardSlugFallback(): string {
    return String(this.generatedDashboardName || this.dashboardName || '')
      .trim()
      .toLowerCase();
  }

  private resetConversation(): void {
    this.messages = [
      {
        id: this.createId(),
        sender: 'agent',
        text: this.selectedEntity
          ? `Upload a file for ${this.selectedEntity}. I will analyze it and surface any missing fields for you to map.`
          : 'Upload a file and I will analyze it and surface any missing fields for you to map.',
        createdAt: new Date().toISOString(),
      },
    ];
    this.analysis = undefined;
    this.fieldSelections = {};
    this.composerText = '';
    this.selectedFiles = [];
    this.uploadError = '';
    this.templateOriginal = null;
    this.templatePreview = null;
    this.templatePreviewDraft = '';
    this.templatePreviewDraftError = '';
    this.templateEntityNameOriginal = '<entity_name>';
    this.templateEntityName = '<entity_name>';
    this.templateKeyMappings = [];
    this.templateSearch = '';
    this.templateValidationErrors = [];
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.entityOptions = [];
    this.selectedTemplateEntity = '';
    this.entityColumns = [];
    this.entityRowsPreview = [];
    this.entityLoadError = '';
    this.isEntityOptionsLoading = false;
    this.isEntityColumnsLoading = false;
    this.isTemplateSaving = false;
    this.generatedDashboardName = '';
    this.generatedDashboardSlug = '';
    this.savedTemplateSnapshot = '';
    this.updateConversationSubtitle('Waiting for file');
    this.missingFieldSelectionsChange.emit({});
  }

  private initializeFieldSelections(missingFields: SelfServiceChatMissingField[]): void {
    const nextSelections: Record<string, string> = {};
    missingFields.forEach((field) => {
      nextSelections[field.key] = this.fieldSelections[field.key] || '';
    });
    this.fieldSelections = nextSelections;
    this.missingFieldSelectionsChange.emit({ ...this.fieldSelections });
  }

  private pushMessage(payload: Pick<SelfServiceChatMessage, 'sender' | 'text' | 'files'>): void {
    this.messages = [
      ...this.messages,
      {
        id: this.createId(),
        sender: payload.sender,
        text: payload.text,
        files: payload.files,
        createdAt: new Date().toISOString(),
      },
    ];
  }

  private updateConversationSubtitle(subtitle: string): void {
    const updatedAt = new Date();
    this.conversations[0] = {
      ...this.conversations[0],
      subtitle,
      updatedAt: updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      active: true,
    };
  }

  private revokeAllPreviewUrls(): void {
    this.objectUrlMap.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlMap.clear();
  }

  private createId(): string {
    return `self-service-chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private initializeTemplateEditor(template: Record<string, unknown> | null): void {
    if (!template) {
      this.templateOriginal = null;
      this.templatePreview = null;
      this.templatePreviewDraft = '';
      this.templatePreviewDraftError = '';
      this.templateKeyMappings = [];
      this.selectedTemplateEntity = '';
      this.entityColumns = [];
      this.entityRowsPreview = [];
      return;
    }

    this.templateOriginal = this.cloneRecord(template);
    this.templateEntityNameOriginal = this.extractEntityName(this.templateOriginal);
    this.templateEntityName = this.templateEntityNameOriginal;
    this.selectedTemplateEntity = this.templateEntityNameOriginal !== '<entity_name>'
      ? this.templateEntityNameOriginal
      : '';
    this.templateKeyMappings = this.collectTemplateKeyMappings(this.templateOriginal);
    this.templateValidationErrors = [];
    this.templateValidationMessage = '';
    this.templateSaveMessage = '';
    this.templatePreviewDraftError = '';
    this.savedTemplateSnapshot = '';
    this.rebuildTemplatePreview();
    this.loadEntityOptions();
    if (this.selectedTemplateEntity) {
      void this.loadEntityColumns(this.selectedTemplateEntity);
    }
  }

  private rebuildTemplatePreview(): void {
    if (!this.templateOriginal) {
      this.templatePreview = null;
      this.templatePreviewDraft = '';
      this.templatePreviewDraftError = '';
      return;
    }

    const entityName = this.templateEntityName.trim() || this.templateEntityNameOriginal;
    const renameMap = this.buildSafeRenameMap();
    const labelMap = new Map<string, string>();
    this.templateKeyMappings.forEach((mapping) => {
      const key = renameMap.get(mapping.originalKey) || mapping.originalKey;
      labelMap.set(key, mapping.displayLabel.trim() || mapping.originalDisplayLabel);
    });

    const entityAdjustedTemplate = this.replaceEntityReferences(
      this.cloneRecord(this.templateOriginal),
      this.templateEntityNameOriginal,
      entityName,
    );
    const renamedTemplate = this.renameTemplateKeysAndValues(entityAdjustedTemplate, renameMap);
    this.templatePreview = this.applyDisplayLabels(renamedTemplate, labelMap);
    this.templatePreviewDraft = JSON.stringify(this.templatePreview, null, 2);
    this.templatePreviewDraftError = '';
  }

  private buildSafeRenameMap(): Map<string, string> {
    const renameMap = new Map<string, string>();
    const seen = new Set<string>();

    this.templateKeyMappings.forEach((mapping) => {
      const candidate = mapping.nextKey.trim();
      const validCandidate = candidate
        && this.isValidTargetBusinessKey(candidate)
        && candidate.toLowerCase() !== 'uuid'
        && !seen.has(candidate)
          ? candidate
          : mapping.originalKey;
      renameMap.set(mapping.originalKey, validCandidate);
      seen.add(validCandidate);
    });

    return renameMap;
  }

  private collectTemplateKeyMappings(template: Record<string, unknown>): TemplateKeyMapping[] {
    const labels = new Map<string, string>();
    const keys = new Set<string>();

    const integrations = Array.isArray(template['integrations'])
      ? template['integrations'] as Array<Record<string, unknown>>
      : [];

    integrations.forEach((integration) => {
      const responseMapping = this.asRecord(integration['responseMapping']);
      const display = this.asRecord(integration['display']);
      const request = this.asRecord(integration['request']);
      const sampleResolvedBody = this.asRecord(request?.['sampleResolvedBody']);
      const contextLabel = String(display?.['label'] || integration['componentName'] || '').trim();

      [responseMapping?.['key'], responseMapping?.['options']].forEach((value) => {
        const key = typeof value === 'string' ? value.trim() : '';
        this.addTemplateKeyCandidate(keys, labels, key, contextLabel || this.toDisplayLabel(key));
      });

      Object.keys(sampleResolvedBody || {}).forEach((key) => {
        this.addTemplateKeyCandidate(keys, labels, key, contextLabel || this.toDisplayLabel(key));
      });

      this.collectKeysFromBindings(request?.['filterBindings'], keys, labels);
    });

    const apiBody = this.asRecord(this.asRecord(template['api'])?.['body']);
    Object.keys(apiBody || {}).forEach((key) => {
      this.addTemplateKeyCandidate(keys, labels, key, this.toDisplayLabel(key));
    });

    this.collectKeysFromMappedTemplateValues(template, keys, labels);

    return Array.from(keys)
      .map((key) => ({
        originalKey: key,
        nextKey: key,
        originalDisplayLabel: labels.get(key) || this.toDisplayLabel(key),
        displayLabel: labels.get(key) || this.toDisplayLabel(key),
        usagePaths: this.findUsagePaths(template, key),
      }))
      .sort((left, right) => (
        left.displayLabel.localeCompare(right.displayLabel)
        || left.originalKey.localeCompare(right.originalKey)
      ));
  }

  private collectKeysFromBindings(
    value: unknown,
    keys: Set<string>,
    labels: Map<string, string>,
  ): void {
    if (Array.isArray(value)) {
      value.forEach((entry) => this.collectKeysFromBindings(entry, keys, labels));
      return;
    }

    if (!value || typeof value !== 'object') {
      return;
    }

    const record = value as Record<string, unknown>;
    Object.entries(record).forEach(([entryKey, entryValue]) => {
      if (typeof entryValue === 'string') {
        this.addTemplateKeyCandidate(keys, labels, entryValue.trim(), this.toDisplayLabel(entryValue.trim()));
      }

      this.addTemplateKeyCandidate(keys, labels, entryKey, this.toDisplayLabel(entryKey));

      this.collectKeysFromBindings(entryValue, keys, labels);
    });
  }

  private collectKeysFromMappedTemplateValues(
    value: unknown,
    keys: Set<string>,
    labels: Map<string, string>,
    path = '$',
    inheritedLabel = '',
  ): void {
    if (Array.isArray(value)) {
      value.forEach((entry, index) => this.collectKeysFromMappedTemplateValues(entry, keys, labels, `${path}[${index}]`, inheritedLabel));
      return;
    }

    const record = this.asRecord(value);
    if (!record) {
      return;
    }

    const contextLabel = this.resolveTemplateContextLabel(record) || inheritedLabel;
    Object.entries(record).forEach(([entryKey, entryValue]) => {
      const nextPath = `${path}.${entryKey}`;
      if (typeof entryValue === 'string' && SelfServiceChatComponent.TEMPLATE_MAPPING_VALUE_KEYS.has(entryKey)) {
        this.addTemplateKeyCandidate(keys, labels, entryValue.trim(), contextLabel || this.toDisplayLabel(entryValue.trim()));
      }

      if (this.isBackendMappingPlaceholder(entryKey) && this.isLikelyTemplateDataPath(path)) {
        this.addTemplateKeyCandidate(keys, labels, entryKey, contextLabel || this.toDisplayLabel(entryKey));
      }

      this.collectKeysFromMappedTemplateValues(entryValue, keys, labels, nextPath, contextLabel);
    });
  }

  private findUsagePaths(template: Record<string, unknown>, key: string): string[] {
    const matches: string[] = [];
    const visit = (value: unknown, path: string): void => {
      if (Array.isArray(value)) {
        value.forEach((entry, index) => visit(entry, `${path}[${index}]`));
        return;
      }

      if (value && typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([entryKey, entryValue]) => {
          const nextPath = path ? `${path}.${entryKey}` : entryKey;
          if (entryKey === key) {
            matches.push(`${nextPath} (property)`);
          }
          visit(entryValue, nextPath);
        });
        return;
      }

      if (typeof value === 'string' && this.stringContainsToken(value, key)) {
        matches.push(path);
      }
    };

    visit(template, '$');
    return matches;
  }

  private replaceEntityReferences(
    value: unknown,
    originalEntityName: string,
    nextEntityName: string,
  ): unknown {
    if (Array.isArray(value)) {
      return value.map((entry) => this.replaceEntityReferences(entry, originalEntityName, nextEntityName));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, entryValue]) => {
        if (key === 'entityName' && typeof entryValue === 'string') {
          result[key] = nextEntityName;
        } else if (key === 'url' && typeof entryValue === 'string') {
          result[key] = this.replaceEntityInUrl(entryValue, originalEntityName, nextEntityName);
        } else {
          result[key] = this.replaceEntityReferences(entryValue, originalEntityName, nextEntityName);
        }
        return result;
      }, {});
    }

    if (typeof value === 'string') {
      if (value.trim() === '<entity_name>' || value.trim() === originalEntityName) {
        return nextEntityName;
      }
      return this.replaceEntityInUrl(value, originalEntityName, nextEntityName);
    }

    return value;
  }

  private renameTemplateKeysAndValues(
    value: unknown,
    renameMap: Map<string, string>,
  ): Record<string, unknown> {
    const renamed = this.renameNode(value, renameMap);
    return this.asRecord(renamed) || {};
  }

  private renameNode(value: unknown, renameMap: Map<string, string>): unknown {
    if (Array.isArray(value)) {
      return value.map((entry) => this.renameNode(entry, renameMap));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, entryValue]) => {
        const nextKey = renameMap.get(key) || key;
        result[nextKey] = this.renameNode(entryValue, renameMap);
        return result;
      }, {});
    }

    if (typeof value === 'string') {
      return this.replaceBusinessKeysInString(value, renameMap);
    }

    return value;
  }

  private applyDisplayLabels(
    template: Record<string, unknown>,
    labelMap: Map<string, string>,
  ): Record<string, unknown> {
    const integrations = Array.isArray(template['integrations'])
      ? template['integrations'] as Array<Record<string, unknown>>
      : [];

    return {
      ...template,
      integrations: integrations.map((integration) => {
        const responseMapping = this.asRecord(integration['responseMapping']);
        const display = this.asRecord(integration['display']);
        const currentKey = String(responseMapping?.['key'] || responseMapping?.['options'] || '').trim();
        const nextLabel = currentKey ? labelMap.get(currentKey) : '';

        if (!nextLabel) {
          return integration;
        }

        return {
          ...integration,
          componentName: nextLabel,
          display: {
            ...(display || {}),
            label: nextLabel,
          },
        };
      }),
    };
  }

  private extractEntityName(template: Record<string, unknown>): string {
    const rootApiEntityName = this.asRecord(template['api'])?.['entityName'];
    if (typeof rootApiEntityName === 'string' && rootApiEntityName.trim()) {
      return rootApiEntityName.trim();
    }

    const integrations = Array.isArray(template['integrations'])
      ? template['integrations'] as Array<Record<string, unknown>>
      : [];
    for (const integration of integrations) {
      const entityName = this.asRecord(integration['api'])?.['entityName'];
      if (typeof entityName === 'string' && entityName.trim()) {
        return entityName.trim();
      }
    }

    return '<entity_name>';
  }

  private replaceEntityInUrl(url: string, originalEntityName: string, nextEntityName: string): string {
    return url.replace(/\/spriced\/platform\/([^/]+)(?=\/)/g, (match, entitySegment) => (
      entitySegment === '<entity_name>' || entitySegment === originalEntityName
        ? `/spriced/platform/${nextEntityName}`
        : match
    ));
  }

  private replaceBusinessKeysInString(value: string, renameMap: Map<string, string>): string {
    let nextValue = value;
    renameMap.forEach((nextKey, originalKey) => {
      if (originalKey === nextKey) {
        return;
      }

      const pattern = new RegExp(`(?<![A-Za-z0-9_])${this.escapeRegExp(originalKey)}(?![A-Za-z0-9_])`, 'g');
      nextValue = nextValue.replace(pattern, nextKey);
    });
    return nextValue;
  }

  private stringContainsToken(value: string, key: string): boolean {
    const pattern = new RegExp(`(?<![A-Za-z0-9_])${this.escapeRegExp(key)}(?![A-Za-z0-9_])`);
    return pattern.test(value);
  }

  private addTemplateKeyCandidate(
    keys: Set<string>,
    labels: Map<string, string>,
    candidate: string,
    label: string,
  ): void {
    const normalized = candidate.trim();
    if (!this.isEditableTemplateKeyCandidate(normalized)) {
      return;
    }

    keys.add(normalized);
    if (!labels.has(normalized)) {
      labels.set(normalized, label.trim() || this.toDisplayLabel(normalized));
    }
  }

  private shouldAutoMatchTemplateKey(mapping: TemplateKeyMapping): boolean {
    const nextKey = mapping.nextKey.trim();
    return !nextKey
      || nextKey === mapping.originalKey
      || this.isBackendMappingPlaceholder(nextKey)
      || !this.entityColumns.includes(nextKey);
  }

  private getRankedTemplateKeySuggestions(mapping: TemplateKeyMapping): Array<{ column: string; score: number }> {
    const uniqueColumns = new Set<string>();
    const ranked = this.entityColumns
      .map((column) => ({
        column,
        score: this.scoreEntityColumnForTemplateKey(column, mapping),
      }))
      .filter((candidate) => candidate.score > 0)
      .sort((left, right) => right.score - left.score || left.column.localeCompare(right.column))
      .filter((candidate) => {
        if (uniqueColumns.has(candidate.column)) {
          return false;
        }
        uniqueColumns.add(candidate.column);
        return true;
      });

    return ranked;
  }

  private scoreEntityColumnForTemplateKey(column: string, mapping: TemplateKeyMapping): number {
    const normalizedColumn = this.normalizeTemplateMatchValue(column);
    const normalizedLabel = this.normalizeTemplateMatchValue(mapping.displayLabel);
    const normalizedOriginalKey = this.normalizeTemplateMatchValue(mapping.originalKey);

    if (!normalizedColumn) {
      return 0;
    }
    if (normalizedColumn === normalizedLabel || normalizedColumn === normalizedOriginalKey) {
      return 120;
    }
    if (normalizedLabel && (normalizedColumn.includes(normalizedLabel) || normalizedLabel.includes(normalizedColumn))) {
      return 95;
    }
    if (normalizedOriginalKey && !this.isBackendMappingPlaceholder(mapping.originalKey)
      && (normalizedColumn.includes(normalizedOriginalKey) || normalizedOriginalKey.includes(normalizedColumn))) {
      return 85;
    }

    const columnTokens = new Set(this.tokenizeTemplateMatchValue(column));
    const labelTokens = this.tokenizeTemplateMatchValue(mapping.displayLabel);
    const originalTokens = this.isBackendMappingPlaceholder(mapping.originalKey)
      ? []
      : this.tokenizeTemplateMatchValue(mapping.originalKey);
    const tokenSet = [...new Set([...labelTokens, ...originalTokens])];
    const overlap = tokenSet.filter((token) => columnTokens.has(token));
    if (!overlap.length) {
      return 0;
    }

    return 40 + overlap.length * 16;
  }

  private normalizeTemplateMatchValue(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private tokenizeTemplateMatchValue(value: string): string[] {
    return this.normalizeTemplateMatchValue(value)
      .split(' ')
      .filter((token) => token.length >= 2);
  }

  private resolveTemplateContextLabel(record: Record<string, unknown>): string {
    const displayLabel = String(this.asRecord(record['display'])?.['label'] || '').trim();
    if (displayLabel) {
      return displayLabel;
    }

    const name = String(record['name'] || record['title'] || record['componentName'] || '').trim();
    return name;
  }

  private isLikelyTemplateDataPath(path: string): boolean {
    return path.includes('.sampleResolvedBody')
      || path.includes('.data[')
      || path.includes('.rows[')
      || path.includes('.body[');
  }

  private isEditableTemplateKeyCandidate(value: string): boolean {
    const normalized = value.trim();
    return !!normalized
      && normalized !== 'uuid'
      && normalized !== '<selected value>'
      && normalized !== '<entity_name>'
      && (this.isValidTargetBusinessKey(normalized) || this.isBackendMappingPlaceholder(normalized));
  }

  private isValidTargetBusinessKey(value: string): boolean {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(value.trim());
  }

  private isBackendMappingPlaceholder(value: string): boolean {
    return value.trim().startsWith('MISSING_BACKEND_MAPPING');
  }

  private toDisplayLabel(value: string): string {
    return value
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
    return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
  }

  private parseTemplateDraft(value: string): Record<string, unknown> | null {
    const raw = String(value || '').trim();
    if (!raw) {
      this.templatePreviewDraftError = 'The submitPayloadTemplate JSON editor cannot be empty.';
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        this.templatePreviewDraftError = 'The submitPayloadTemplate JSON editor must contain a JSON object.';
        return null;
      }

      this.templatePreviewDraftError = '';
      return parsed as Record<string, unknown>;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON.';
      this.templatePreviewDraftError = `JSON editor error: ${message}`;
      return null;
    }
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
